import Rollbar from 'rollbar';

import {
  Injectable,
  Inject,
  InjectionToken,
  ErrorHandler
} from '@angular/core';
import {environment} from '../environments/environment';
import posthog from 'posthog-js';

let env: string;
switch (window.location.hostname) {
  case "perf.soprism.com":
    env = 'perf';
    break;
  case "app.soprism.com":
    env = 'production';
    break;
  case "soprism.audiense.com":
    env = 'production';
    break;
  case "dev.soprism.com":
    env = 'dev';
    break;
  case "demo.soprism.com":
    env = 'demo';
    break;
  default:
    env = 'local';
}

export const RollbarService = new InjectionToken<Rollbar>('rollbar');

const IGNORED_MESSAGES = [
  'Highcharts error #13: www.highcharts.com/errors/13/',
  'ChunkLoadError',
  'MetaMask',
  'bufferBelongsToIframe',
  'Response body is already used'
];
const IGNORED_MESSAGES_REGEX = [
  /Highcharts error #13/,
  /ChunkLoadError/,
  /Loading chunk \d+ failed/,
  /MetaMask/,
  /bufferBelongsToIframe/,
  /Response body is already used/,
  /^Failed to fetch$/,
  /^Load failed$/,
  /NetworkError when attempting to fetch resource/,
  /The user aborted a request/,
  /signal is aborted without reason/,
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  /^Script error\.?$/
];

const IGNORED_ERROR_NAMES = ['AbortError', 'InterruptError'];
const IGNORED_HTTP_STATUSES = [401, 403, 404, 409];
const UI_HANDLED_ERROR_CODE_PREFIX = 'CAPI_';
const EXTENSION_URL_REGEX = /(chrome|moz|safari(-web)?)-extension:\/\//;

@Injectable()
export class CentralizedErrorHandler implements ErrorHandler {
  constructor(@Inject(RollbarService) private rollbar: Rollbar) {}

  handleError(err: any): void {
    console.log(err);

    // Unwrap Angular's zone.js error wrapper to get the actual error
    // BUT: don't unwrap .error for HttpErrorResponse as that's the response body
    let actualError = err.rejection || err.originalError || err;
    if (actualError?.name !== 'HttpErrorResponse') {
      actualError = actualError.error || actualError;
    }

    // Filter out browser extension errors
    const stack = actualError?.stack || err?.stack || '';
    if (stack.includes('chrome-extension://') || stack.includes('moz-extension://')) {
      console.log('[ErrorHandler] Ignoring browser extension error:', actualError?.message);
      return;
    }

    if (IGNORED_ERROR_NAMES.includes(actualError?.name)) {
      return;
    }

    // Check if error should be ignored
    const errorMessage = actualError?.message || String(actualError);
    if (IGNORED_MESSAGES.some(msg => errorMessage.includes(msg))) {
      return;
    }
    if (IGNORED_MESSAGES_REGEX.some(regex => regex.test(errorMessage))) {
      return;
    }

    // Handle HttpErrorResponse specially to extract meaningful error info
    let errorToReport = actualError;
    const customProperties: any = {};

    if (actualError?.name === 'HttpErrorResponse' && actualError.status === 0) {
      console.log('[ErrorHandler] Network failure (status 0), not reporting to Rollbar');
      return;
    }

    if (actualError?.name === 'HttpErrorResponse' && actualError.error !== undefined && !(actualError.error instanceof ProgressEvent)) {
      // Filter out expected HTTP errors that are normal business logic
      if (IGNORED_HTTP_STATUSES.includes(actualError.status)) {
        console.log('[ErrorHandler] Ignoring expected HTTP error:', actualError.status, actualError.statusText);
        return;
      }
      if (String(actualError.error?.code ?? '').startsWith(UI_HANDLED_ERROR_CODE_PREFIX)) {
        return;
      }


      // Extract the actual business error from the HTTP response
      const backendError = actualError.error;

      let meaningfulMessage: string;
      let errorCode: string | undefined;
      let errorReason: string | undefined;

      if (backendError == null) {
        // Null/undefined body (e.g., empty 500 response from Akka HTTP)
        meaningfulMessage = `HTTP_ERROR: ${actualError.statusText}`;
      } else if (typeof backendError === 'string') {
        // Plain text error body (e.g., stats API returns "One or both audiences do not have crawl data")
        meaningfulMessage = backendError;
      } else if (backendError.error instanceof SyntaxError || (backendError.error && backendError.text !== undefined)) {
        // JSON parse failure from Angular: {error: SyntaxError, text: "raw body"}
        // This happens when a 200 response has a non-JSON body
        meaningfulMessage = `JSON parse error: ${backendError.error?.message || backendError.text}`;
      } else {
        // Structured backend error object with {message, code, reason}
        meaningfulMessage = backendError.message ||
                           `${backendError.code || 'HTTP_ERROR'}: ${backendError.reason || actualError.statusText}`;
        errorCode = backendError.code;
        errorReason = backendError.reason;
      }

      // Create new error with meaningful message
      errorToReport = new Error(meaningfulMessage);
      errorToReport.name = errorCode || 'HttpError';

      // Preserve the original stack trace from the HttpErrorResponse
      if (actualError.stack) {
        errorToReport.stack = actualError.stack;
      }

      // Add HTTP context as custom properties
      customProperties.httpStatus = actualError.status;
      customProperties.httpUrl = actualError.url;
      customProperties.httpStatusText = actualError.statusText;
      customProperties.errorCode = errorCode;
      customProperties.errorReason = errorReason;

      // Include any extra args if present
      if (backendError && typeof backendError === 'object' && backendError.extraArgs) {
        customProperties.extraArgs = backendError.extraArgs;
      }
    } else {
      // Extract custom properties from the error for non-HTTP errors
      if (actualError && typeof actualError === 'object') {
        Object.keys(actualError).forEach(key => {
          if (key !== 'name' && key !== 'message' && key !== 'stack') {
            customProperties[key] = actualError[key];
          }
        });
      }
    }

    // Send error to Rollbar if enabled
    if (environment.config.rollbar?.enabled) {
      this.rollbar.error(errorToReport, customProperties);
    }

    // Send error to Posthog if enabled
    if (environment.config.posthog?.enabled) {
      posthog.captureException(errorToReport, customProperties);
    }
  }
}

export function isExtensionPayload(_isUncaught: boolean, _args: any[], payload: any): boolean {
  const body = payload?.body;
  const traces = [body?.trace, ...(body?.trace_chain ?? [])].filter(Boolean);
  const frameFilenames = traces.flatMap(trace => (trace.frames ?? []).map((frame: any) => frame?.filename ?? ''));
  const stacks = [
    ...traces.map(trace => trace.exception?.stack ?? ''),
    payload?.notifier?.diagnostic?.raw_error?.stack ?? ''
  ];

  return [...frameFilenames, ...stacks].some(text => EXTENSION_URL_REGEX.test(text));
}

export function rollbarFactory() {
  const rollbarConfig: Rollbar.Configuration = {
    accessToken: '319072c83e31473599388ed70402a082',
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: env,
    enabled: true,
    ignoredMessages: IGNORED_MESSAGES_REGEX,
    checkIgnore: (isUncaught: boolean, args: any[], payload: any) => {
      // Ignore errors from browser extensions
      if (isExtensionPayload(isUncaught, args, payload)) {
        return true; // Ignore this error
      }

      return false; // Don't ignore
    },
    payload: {
      context: () => window.location.pathname,
      client: {
        javascript: {
          code_version: env,
          source_map_enabled: true,
          guess_uncaught_frames: true
        }
      },
      custom: {
        url: () => window.location.href,
        userAgent: () => navigator.userAgent,
        timestamp: () => new Date().toISOString(),
        viewport: () => `${window.innerWidth}x${window.innerHeight}`,
        extensions: () => {
          // Detect common extensions that might interfere
          const extensionIndicators = [];
          if (document.querySelector('[data-extension]')) extensionIndicators.push('generic-extension');
          if ((window as any).chrome?.runtime) extensionIndicators.push('chrome-extension-api');
          return extensionIndicators.length > 0 ? extensionIndicators : null;
        }
      }
    },
    transform: (payload: any) => {
      // Add specific context for Response clone errors  
      if (payload.body?.trace?.exception?.message?.includes('Response body is already used')) {
        payload.custom = payload.custom || {};
        payload.custom.extensionInterference = true;
        payload.custom.errorType = 'extension-response-clone';
        
        // Capture recent network activity to help identify the source
        payload.custom.recentNetworkActivity = [];
        try {
          // Get recent performance entries for network requests
          const entries = performance.getEntriesByType('navigation').concat(
            performance.getEntriesByType('resource')
          ).slice(-10); // Last 10 network activities
          
          payload.custom.recentNetworkActivity = entries.map((entry: any) => ({
            name: entry.name,
            type: entry.entryType,
            startTime: entry.startTime,
            duration: entry.duration
          }));
        } catch (e) {
          payload.custom.recentNetworkActivity = ['Failed to capture network activity'];
        }
      }
      return payload;
    }
  };

  return new Rollbar(rollbarConfig);
}
